import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { MOCK_AUTH_DIAGNOSTICS, MOCK_AUTH_DIAGNOSTIC_LOGS, AUTH_DIAG_LOG_FILTER_EVENTS, AUTH_DIAG_LOG_FILTER_USERS } from "@/data/mock";
import { usePermission } from "@/hooks/use-permission";

/* AUTH-CFG-04 — Auth Diagnostics.
   Canonical source: prototype/pages/admin/auth-diagnostics.html (no
   current Figma frame located). Two source-defined states: "Default"
   (3 static diagnostic test cards) and "Filtered Logs" (date range +
   event/user filters + a login-event table). Both implemented — this is
   the one AUTH-CFG screen whose non-happy "state" (Filtered Logs) is
   actually a second real, source-defined view, not an exception/empty/
   permission state, so it was built (unlike every other AUTH-CFG
   non-happy state, none of which exist in this source at all).
   Each "Run Test" button re-displays the SAME static, illustrative pass
   result after a brief delay — it does not run any real check and does
   not fabricate a different outcome; this mirrors the canonical
   prototype's own behavior exactly (`testSSO()`/`testMFA()`/
   `testSessions()` in the source do the identical hide-then-reshow of a
   static div). "Export Logs" reproduces the canonical source's own
   honest toast verbatim ("Exporting logs as CSV (production only)") —
   the prototype itself defers this, it is not a deferral invented here. */

type DiagKey = "sso" | "mfa" | "session";

export function AuthDiagnostics() {
  const canRun = usePermission("auth:configure");
  const [view, setView] = useState<"default" | "filtered">("default");
  const [visible, setVisible] = useState<Record<DiagKey, boolean>>({ sso: true, mfa: true, session: true });
  const [testing, setTesting] = useState<Record<DiagKey, boolean>>({ sso: false, mfa: false, session: false });
  const [eventFilter, setEventFilter] = useState("Login Success");
  const [userFilter, setUserFilter] = useState("");

  const delays: Record<DiagKey, number> = { sso: 1200, mfa: 1400, session: 1000 };

  function runTest(key: DiagKey) {
    setTesting((t) => ({ ...t, [key]: true }));
    setVisible((v) => ({ ...v, [key]: false }));
    setTimeout(() => {
      setVisible((v) => ({ ...v, [key]: true }));
      setTesting((t) => ({ ...t, [key]: false }));
    }, delays[key]);
  }

  const filteredLogs = useMemo(
    () => MOCK_AUTH_DIAGNOSTIC_LOGS.filter((l) => (!userFilter || l.user === userFilter)),
    [userFilter]
  );

  function exportLogs() {
    toast({ variant: "info", title: "Exporting logs as CSV (production only)" });
  }

  const cards: { key: DiagKey; label: string }[] = [
    { key: "sso", label: "sso" },
    { key: "mfa", label: "mfa" },
    { key: "session", label: "session" },
  ];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Auth Diagnostics</h1>
          <p className="mt-1 text-sm text-muted">Run live checks against your SSO, MFA, and session configuration.</p>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setView("default")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "default" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
          >
            Default
          </button>
          <button
            type="button"
            onClick={() => setView("filtered")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "filtered" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
          >
            Filtered Logs
          </button>
        </div>
      </div>

      {view === "default" && (
        <div className="space-y-4">
          {cards.map(({ key }) => {
            const c = MOCK_AUTH_DIAGNOSTICS[key];
            return (
              <div key={key} className="rounded-[10px] border border-line bg-surface p-5">
                <div className="mb-1 text-base font-bold text-ink">{c.title}</div>
                <p className="mb-3 text-sm text-muted">{c.description}</p>
                <Button variant="secondary" size="sm" disabled={testing[key] || !canRun} onClick={() => canRun && runTest(key)}>
                  {testing[key] ? "Testing…" : c.buttonLabel}
                </Button>
                {visible[key] && (
                  <div className="mt-3 rounded-[6px] border border-success-border bg-success-soft px-3 py-2 text-sm font-semibold text-success">
                    ✓ {c.resultText}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "filtered" && (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <input type="date" defaultValue="2026-09-12" aria-label="From date" className="h-[34px] rounded-[6px] border border-line-strong bg-surface px-2 text-sm text-ink outline-none" />
            <span className="text-sm text-muted">to</span>
            <input type="date" defaultValue="2026-09-14" aria-label="To date" className="h-[34px] rounded-[6px] border border-line-strong bg-surface px-2 text-sm text-ink outline-none" />
            <Select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} aria-label="Filter by event" className="w-[170px]">
              <option value="">All Events</option>
              {AUTH_DIAG_LOG_FILTER_EVENTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
            <Select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} aria-label="Filter by user" className="w-[170px]">
              <option value="">All Users</option>
              {AUTH_DIAG_LOG_FILTER_USERS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
            <Button variant="secondary" size="sm" disabled={!canRun} onClick={() => canRun && exportLogs()}>Export Logs</Button>
          </div>

          <div className="mb-2 text-xs text-muted">Showing {filteredLogs.length} event{filteredLogs.length === 1 ? "" : "s"} in the selected range</div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-3 py-2">Timestamp</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">IP</th>
                <th className="px-3 py-2">Details</th>
                <th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((l, i) => (
                <tr key={i} className="border-b border-line last:border-b-0">
                  <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-ink">{l.timestamp}</td>
                  <td className="px-3 py-2.5 font-semibold text-ink">{l.user}</td>
                  <td className="px-3 py-2.5 text-ink">{l.event}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-muted">{l.ip}</td>
                  <td className="px-3 py-2.5 text-xs text-muted">{l.details}</td>
                  <td className="px-3 py-2.5"><Badge variant="success" size="sm">{l.result}</Badge></td>
                </tr>
              ))}
              {filteredLogs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted">No events match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6">
        <Link to="/admin/auth-config" className="text-sm font-semibold text-primary hover:underline">← Back to Auth Configuration</Link>
      </div>
    </div>
  );
}
